import { type Ref,watch } from 'vue'
import { useDemandeAFStore } from '@/features/interface/demande/af/useDemandeAFStore.ts'
import type { FraisAnnexeDTO } from '@/features/api/extranet/dto/Demande/AF'
import { storeToRefs } from 'pinia'
import type { UniqueID } from '@/features/domaine/types/techniques'
import type { FraisDTO } from '@/features/api/extranet/dto/Demande/shared'
import type { NatureFraisAnnexeModele } from '@/features/interface/demande/af/steps/frais/types.ts'


/**
 * Surveille les changements des salariés et des modules pour synchroniser les frais annexes.
 * Initialise les nouveaux frais pour chaque combinaison salarié/module manquante et supprime les orphelins.
 *
 * @param naturesFrais - Référence vers la liste des natures de frais annexes à appliquer par défaut.
 */
export const useWatchSalariesModules = (naturesFrais: Ref<NatureFraisAnnexeModele[]>) => {
  const store = useDemandeAFStore()
  const { setupCustomUpdater } = store
  const { model } = storeToRefs(store)

  type SalarieModuleId = { salarieId: UniqueID, moduleId: UniqueID }
  const patchFrais = setupCustomUpdater<[SalarieModuleId[], SalarieModuleId[]]>(
    (patch, [ajouts, orphelins]) => {
      // Retire les orphelins
      for (const item of orphelins) {
        const index = patch.fraisAnnexes!.findIndex(
          (frais) => frais.salarieId === item.salarieId && frais.moduleId === item.moduleId,
        )
        patch.fraisAnnexes!.splice(index, 1)
      }

      // Traite les ajouts
      patch.fraisAnnexes!.push(
        ...ajouts.map(({ salarieId, moduleId }) => {
          return {
            salarieId,
            moduleId,
            frais: naturesFrais.value.map(
              ({ baseCalcul, nature }) =>
                ({
                  nature,
                  baseCalcul,
                  mode: 'Annexe',
                  montant: null,
                  quantite: null,
                }) as FraisDTO,
            ),
          } as FraisAnnexeDTO
        }),
      )
    })

  watch(
    () => (store.model?.salaries?.length ?? 0) * (store.model?.formation?.modules?.length ?? 0),
    async () => {
      const modules = model.value?.formation?.modules ?? []
      const salaries = model.value?.salaries ?? []
      const fraisAnnexes = model.value?.fraisAnnexes ?? []

      const produitCartésien = modules.flatMap((module) =>
        salaries.map((salarie) => ({ salarieId: salarie.id, moduleId: module.id })),
      )
      const ajouts = produitCartésien.filter(
        ({ salarieId, moduleId }) =>
          !fraisAnnexes!.some((frais) => frais.moduleId === moduleId && frais.salarieId === salarieId),
      )

      const orphelins = fraisAnnexes.filter(
        (frais) =>
          !produitCartésien.some(
            ({ salarieId, moduleId }) => salarieId === frais.salarieId && moduleId === frais.moduleId,
          ),
      )

      if (ajouts.length === 0 && orphelins.length === 0) return

      await patchFrais([ajouts, orphelins])
    },
    { immediate: true },
  )
}
